import React, { useEffect, useRef } from 'react';
import { contrastColor, green200 } from '@mentimeter/ragnar-colors';
import { CheckIcon } from '@mentimeter/ragnar-visuals';
import { Box } from '@mentimeter/ragnar-ui/box';
import { Text } from '@mentimeter/ragnar-ui/text';
import { useTranslations } from 'next-intl';
import { usePresentationContext } from '../../../legacy-context/presentationContext';
import { FocusNotice } from './FocusNotice';

// Needs to be a type instead of interface as the interface can't satisfy the generic used with useSlideState hook
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export type EmbeddedSlideState = {
  isPresenting?: boolean;
  canPresent?: boolean;
};

type IntegrationName = 'Google Slides' | 'PowerPoint';

const START_PRESENTATION_TRANSITION = '.5s cubic-bezier(0.33, 1, 0.68, 1)';

interface PropsT {
  presentationUrl: string;
  isValidUrl: (url: string) => boolean;
  integrationName: IntegrationName;
  focusNoticeEnabled?: boolean;
}

export function EmbedIntegrationPresentation({
  presentationUrl,
  isValidUrl,
  integrationName,
  focusNoticeEnabled = false,
}: PropsT) {
  const translate = useTranslations('presenting.embed_integration');
  const { useConfig, useSlideState } = usePresentationContext();
  const { mode, enableEmbedSlides, readonly } = useConfig();

  const iframe = useRef<HTMLIFrameElement>(null);

  const [{ isPresenting = false, canPresent = false }, setSlideState] =
    useSlideState<EmbeddedSlideState>();

  const embedSlidesNoticeTextColor = contrastColor(green200);
  const validUrl = isValidUrl(presentationUrl);

  const canInteractWithEmbeddedPresentation =
    validUrl && (mode === 'live' || mode === 'public' || mode === 'preview');
  const inScreenshotMode = mode === 'screenshot';
  const inEditView = mode === 'idle';

  const canViewPresentation = !inScreenshotMode && enableEmbedSlides;

  useEffect(() => {
    if (canPresent !== canInteractWithEmbeddedPresentation) {
      setSlideState({
        canPresent: canInteractWithEmbeddedPresentation,
      });
    }
  });

  useEffect(() => {
    if (isPresenting) {
      if (iframe.current) {
        if (!focusNoticeEnabled) {
          iframe.current.focus();
        }
      }
    } else {
      if (iframe.current) {
        iframe.current.blur();
      }
    }
  }, [
    canPresent,
    focusNoticeEnabled,
    integrationName,
    isPresenting,
    setSlideState,
  ]);

  return (
    <Box
      data-testid="embed-integration-presentation"
      width="100%"
      height="100%"
      alignItems="center"
      justifyContent="center"
    >
      {inScreenshotMode && (
        <Text fontSize={6}>{translate('only_viewable_in_app_message')}</Text>
      )}

      {!enableEmbedSlides && (
        <div>
          <Text fontSize="84px">{integrationName}</Text>
        </div>
      )}

      {canViewPresentation &&
        (validUrl ? (
          <Box
            extend={() => ({
              position: 'fixed',
              top: '50%',
              left: '50%',
              width: '100%',
              height: '100%',
              transform: `translate(-50%, -50%)`,
              overflow: 'hidden',
            })}
          >
            <Box
              extend={() => ({
                position: 'absolute',
                top: '0',
                left: '0',
                right: '0',
                bottom: '0',
                transition: 'transform ' + START_PRESENTATION_TRANSITION,
                transform: `scale(${!isPresenting ? '.7' : '1'})`,
              })}
            >
              <Box
                extend={() => ({
                  width: '100%',
                  height: '100%',
                  overflow: 'hidden',
                })}
              >
                <iframe
                  ref={iframe}
                  src={presentationUrl}
                  frameBorder="0"
                  allowFullScreen={false}
                  onBlur={() => {
                    /**
                     * When we stop presenting from the controls this event runs before that,
                     *  which causes the controls to instead start the presentation again.
                     * Therefore we wait and run this after the control action has finished.
                     **/
                    setTimeout(stopPresenting, 250);
                  }}
                  style={{
                    width: '100%',
                    height: '100%',
                    background: 'black',
                  }}
                />
              </Box>
              {!isPresenting && (
                <Box
                  onClick={startPresentation}
                  extend={() => ({
                    position: 'absolute',
                    width: '100%',
                    height: '100%',
                  })}
                />
              )}

              <Box
                onClick={(e) => {
                  e.stopPropagation();
                }}
                extend={({ theme }) => ({
                  position: 'absolute',
                  top: '-4px',
                  left: '-4px',
                  right: '-4px',
                  bottom: '-4px',
                  border: `16px solid ${theme.colors.border}`,
                  borderRadius: `${theme.radii[2]}`,
                  pointerEvents: 'none',
                  opacity: isPresenting ? 0 : 1,
                  transition: isPresenting
                    ? 'opacity 1.6s ease-in'
                    : 'opacity .2s ease-out',
                })}
              />

              {inEditView && (
                <Box
                  flexDirection="row"
                  alignItems="center"
                  mx="auto"
                  px={4}
                  py={3}
                  borderRadius={1}
                  bg={green200}
                  position="absolute"
                  top="0"
                  left="50%"
                  extend={() => ({
                    transform: 'translate(-50%, -50%)',
                  })}
                >
                  <Box mr={3}>
                    <CheckIcon color={embedSlidesNoticeTextColor} size={5} />
                  </Box>
                  <Text fontSize={5} color={embedSlidesNoticeTextColor}>
                    {translate('contains_presentation_message', {
                      integration: integrationName,
                    })}
                  </Text>
                </Box>
              )}
            </Box>
          </Box>
        ) : (
          <Box
            style={{
              position: 'fixed',
              top: '0',
              left: '0',
              bottom: '0',
              right: '0',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Text
              style={{
                fontSize: 42,
              }}
            >
              {translate('link_presentation_instruction_message', {
                integration: integrationName,
              })}
            </Text>
          </Box>
        ))}

      {!readonly && isPresenting && focusNoticeEnabled && enableEmbedSlides && (
        <FocusNotice />
      )}
    </Box>
  );

  function startPresentation() {
    setSlideState({ isPresenting: true });
  }

  function stopPresenting() {
    if (isPresenting === true) {
      // TODO: Problem is that the blur event is triggered before the click on the controls button :(
      setSlideState({ isPresenting: false });
    }
  }
}
